import {BasePlugin, type PluginOptions, Uppy} from '@uppy/core';
import hasProperty from '@uppy/utils/lib/hasProperty';
import NetworkError from '@uppy/utils/lib/NetworkError';
import isNetworkError from '@uppy/utils/lib/isNetworkError';
import settle from '@uppy/utils/lib/settle';
import * as tus from 'tus-js-client';

import type {AnyUppyFile} from '../../types';
import {TusQueue} from './tus-queue';

const tusDefaultOptions: tus.UploadOptions = {
	addRequestId: false,
	chunkSize: Infinity,
	endpoint: '',
	headers: {},
	metadata: {},
	overridePatchMethod: false,
	parallelUploads: 1,
	removeFingerprintOnSuccess: false,
	retryDelays: [1000, 3000, 7000, 10000, 20000],
	uploadDataDuringCreation: false,
	uploadLengthDeferred: false,
	uploadSize: null,
	uploadUrl: null,

	onChunkComplete: null,
	onError: null,
	onProgress: null,
	onSuccess: null,
};

// eslint-disable-next-line import/namespace
export interface TusOptions extends PluginOptions, tus.UploadOptions {}

export type CustomTusOptions = Omit<
	TusOptions,
	| 'headers'
	| 'onBeforeRequest'
	| 'onError'
	| 'onProgress'
	| 'onShouldRetry'
	| 'onSuccess'
> & {
	allowedMetaFields?: string[];
	headers?:
		| ((file: AnyUppyFile) => tus.UploadOptions['headers'])
		| tus.UploadOptions['headers'];
	id?: string;
	limit?: number;
	useFastRemoteRetry?: boolean;
	withCredentials?: boolean;
	onBeforeRequest?: (req: tus.HttpRequest, file: AnyUppyFile) => Promise<void>;
	onShouldRetry?: (
		error: Error | tus.DetailedError,
		retryAttempt: number,
		options: tus.UploadOptions,
		defaultOnShouldRetry: Required<TusOptions>['onShouldRetry'],
	) => boolean;
};

const copyProp = (obj: object, srcProp: string, destProp: string) => {
	if (hasProperty(obj, srcProp) && !hasProperty(obj, destProp)) {
		// @ts-ignore
		// eslint-disable-next-line no-param-reassign
		obj[destProp] = obj[srcProp];
	}
};

export class Tus extends BasePlugin<CustomTusOptions> {
	opts: CustomTusOptions = {
		limit: 6,
		useFastRemoteRetry: true,
		retryDelays: tusDefaultOptions.retryDelays,
		withCredentials: false,
	};
	title: string;

	queue: TusQueue;
	#retryDelayIterator: IterableIterator<number>;

	constructor(uppy: Uppy, opts: CustomTusOptions) {
		super(uppy, opts);

		this.id = this.opts.id || 'Tus';
		this.opts = {...this.opts, ...opts};
		this.queue = new TusQueue({limit: opts.limit || 6});
		this.title = 'Tus';
		this.type = 'uploader';

		this.#retryDelayIterator = (this.opts.retryDelays || []).values();

		this.handleFileAdded = this.handleFileAdded.bind(this);
		this.handleFileError = this.handleFileError.bind(this);
		this.handleCancelAll = this.handleCancelAll.bind(this);
		this.handlePauseAll = this.handlePauseAll.bind(this);
		this.handleRemove = this.handleRemove.bind(this);
		this.handleResetProgress = this.handleResetProgress.bind(this);
		this.handleResumeAll = this.handleResumeAll.bind(this);
		this.handleUpload = this.handleUpload.bind(this);
		this.handleUploadPause = this.handleUploadPause.bind(this);
	}

	handleCancelAll() {
		this.queue.reset();
	}

	handlePauseAll() {
		this.queue.pauseAllItems();
	}

	handleResetProgress() {
		const files = {...this.uppy.getState().files};
		Object.keys(files).forEach((fileID) => {
			// Only clone the file object if it has a Tus `uploadUrl` attached.
			if ((files[fileID] as any).tus && (files[fileID] as any).tus.uploadUrl) {
				const tusState = {...(files[fileID] as any).tus};
				delete tusState.uploadUrl;
				files[fileID] = {...files[fileID], tus: tusState} as any;
			}
		});

		this.uppy.setState({files});
	}

	handleResumeAll() {
		this.queue.resumeAllItems();
	}

	/**
	 * File upload error
	 * @param fileId Uppy file id
	 * @param err Error
	 */
	handleFileError(fileId: string, err: tus.DetailedError | Error) {
		this.uppy.log(err.message, 'error');

		const xhr =
			err instanceof tus.DetailedError
				? err.originalRequest.getUnderlyingObject()
				: null;

		if (isNetworkError(xhr)) {
			// eslint-disable-next-line no-param-reassign
			err = new NetworkError(err, xhr);
		}

		this.queue.remove(fileId);

		this.uppy.emit('upload-error', this.uppy.getFile(fileId), err);
	}

	handleFileProgress(
		fileId: string,
		bytesUploaded: number,
		bytesTotal: number,
	) {
		this.uppy.emit('upload-progress', this.uppy.getFile(fileId), {
			uploader: this,
			bytesUploaded,
			bytesTotal,
		});
	}

	/**
	 * File was uploaded successfully
	 * @param fileId Uppy file id
	 * @param url Tus file url
	 */
	handleFileSuccess(fileId: string, url?: string | null) {
		this.queue.remove(fileId, true);

		const uploadResp = {
			uploadURL: url,
		};

		this.uppy.emit('upload-success', this.uppy.getFile(fileId), uploadResp);

		if (url) this.uppy.log(`[${this.id}] Download ${fileId} from ${url}`);
	}

	/**
	 * File was removed
	 * @param file Uppy file
	 */
	handleRemove(file: AnyUppyFile) {
		this.queue.remove(file.id);
	}

	async handleUpload(fileIds: string[]) {
		if (fileIds.length === 0) {
			this.uppy.log(`[${this.id}] No files to upload`);
			return;
		}

		if (this.opts.limit === 0) {
			this.uppy.log(
				`[${this.id}] When uploading multiple files at once, consider setting the \`limit\` option (to \`10\` for example), to limit the number of concurrent uploads, which helps prevent memory and network issues: https://uppy.io/docs/tus/#limit-0`,
				'warning',
			);
		}

		this.uppy.log(`[${this.id}] Uploading...`);

		const files = this.uppy.getState().files;

		const filesToUpload = fileIds
			.map((fileID) => files[fileID])
			.filter(
				(file) => file != null && !this.queue.queuedIds.includes(file.id),
			) as AnyUppyFile[];

		return this.uploadFiles(filesToUpload).then((result) => {
			// console.log('result', result);
		});
	}

	/**
	 * File toggled pause
	 * @param fileId Uppy file id
	 * @param isPaused Is paused
	 */
	handleUploadPause(fileId: string, isPaused: boolean) {
		isPaused ? this.queue.pauseItem(fileId) : this.queue.resumeItem(fileId);
	}

	onReceiveUploadUrl(fileId: string, uploadURL: string | null) {
		const currentFile = this.uppy.getFile(fileId);
		if (!currentFile) return;
		// Only do the update if we didn't have an upload URL yet.
		if (
			!(currentFile as any).tus ||
			(currentFile as any).tus.uploadUrl !== uploadURL
		) {
			this.uppy.log(`[${this.id}] Storing upload url`);
			this.uppy.setFileState(currentFile.id, {
				tus: {...(currentFile as any).tus, uploadUrl: uploadURL},
			});
		}
	}

	/**
	 *
	 * @param file
	 */
	handleFileAdded(file: AnyUppyFile) {
		this.queue.updateItem(file.id, {
			file: file.data,
		});
	}

	/**
	 * Upload of inidividual file\
	 * tus-js-client does not support multiple uploads on its own
	 * @param file Uppy file
	 * @param fileNumber Number in list
	 * @param totalFileCount Total in list
	 */
	async upload(file: AnyUppyFile, fileNumber: number, totalFileCount: number) {
		let cleanup = () => {};

		return new Promise<void | tus.Upload | string>((resolve, reject) => {
			const opts: CustomTusOptions = {
				...this.opts,
				...((file as any).tus || {}),
			};

			const metadata: Record<string, string> = {};
			const allowedMetaFields = Array.isArray(opts.allowedMetaFields)
				? opts.allowedMetaFields
				: // Send along all fields by default
				  Object.keys(file.meta || {});

			allowedMetaFields.forEach((item) => {
				// @ts-ignore
				metadata[item] = file.meta[item];
			});

			// tus uses metadata fields 'filetype' and 'filename'
			copyProp(metadata, 'type', 'filetype');
			copyProp(metadata, 'name', 'filename');

			// https://github.com/tus/tus-js-client/blob/master/docs/api.md#onshouldretry
			const defaultOnShouldRetry = (
				error: Error | tus.DetailedError,
				retryAttempt: number,
				options: tus.UploadOptions,
			) => {
				// an upload error is not fired until we run out of retrys/return false

				const status =
					error instanceof tus.DetailedError
						? error.originalResponse?.getStatus()
						: 0;

				// HTTP 429 Too Many Requests => to avoid the whole download to fail, pause all requests.
				if (status === 429) {
					if (!this.queue.paused) {
						const nextDelay = this.#retryDelayIterator.next();
						if (nextDelay == null || nextDelay.done) return false;
						this.queue.pause(nextDelay.value);
					}
				}
				// HTTP 4xx, the server won't send anything, it's doesn't make sense to retry
				else if (
					status != null &&
					status > 400 &&
					status < 500 &&
					status !== 409
				) {
					return false;
				}
				// offline
				else if (
					typeof navigator !== 'undefined' &&
					navigator.onLine === false
				) {
					// The navigator is offline, let's wait for it to come back online.
					if (!this.queue.paused) {
						this.queue.pause();
						window.addEventListener(
							'online',
							this.queue.resume.bind(this.queue),
							{once: true},
						);
					}
				}

				return true;
			};

			const uploadOptions: TusOptions = {
				...tusDefaultOptions,
				...opts,
				// We override tus fingerprint to uppy’s `file.id`, since the `file.id`
				// now also includes `relativePath` for files added from folders.
				// This means you can add 2 identical files, if one is in folder a,
				// the other in folder b.
				fingerprint: async (_file, options) =>
					['tus', file.id, options?.endpoint].join('-'),

				headers:
					typeof opts.headers === 'function'
						? opts.headers(file)
						: opts.headers,

				metadata,

				onBeforeRequest: async (req) => {
					const xhr = req.getUnderlyingObject();
					if (xhr) xhr.withCredentials = !!opts.withCredentials;

					let userProvidedPromise: Promise<void> | undefined;
					if (typeof opts.onBeforeRequest === 'function')
						userProvidedPromise = opts.onBeforeRequest(req, file);

					return userProvidedPromise;
				},

				onError: (err) => {
					this.handleFileError(file.id, err);
					reject(err);
				},

				onProgress: (bytesUploaded, bytesTotal) => {
					this.onReceiveUploadUrl(file.id, upload.url);
					this.handleFileProgress(file.id, bytesUploaded, bytesTotal);
				},

				onShouldRetry: opts.onShouldRetry
					? (error, retryAttempt, options) =>
							opts.onShouldRetry!(
								error,
								retryAttempt,
								options,
								defaultOnShouldRetry,
							)
					: defaultOnShouldRetry,

				onSuccess: () => {
					this.handleFileSuccess(file.id, upload.url);
					resolve(upload);
				},
			};

			const upload = new tus.Upload(file.data, uploadOptions);

			// https://github.com/tus/tus-js-client#example
			upload.findPreviousUploads().then((previousUploads) => {
				const previousUpload = previousUploads[0];
				if (previousUpload) {
					this.uppy.log(
						`[${this.id}] Resuming upload of ${file.id} started at ${previousUpload.creationTime}`,
					);
					upload.resumeFromPreviousUpload(previousUpload);
				}
			});

			this.queue.add({
				id: file.id,
				paused: file.isPaused || file.isGhost,
				uploader: upload,
			});

			// this is the only real funky event we need to listen to on a file level
			// since it will cause a resolve of this inner promise
			const onCancelAll = () => {
				reject(`upload ${file.id} was canceled`);
			};
			this.uppy.on('cancel-all', onCancelAll);
			cleanup = () => {
				this.uppy.off('cancel-all', onCancelAll);
			};
		}).finally(cleanup);
	}

	/**
	 * Upload files
	 * @param files Uppy files
	 */
	async uploadFiles(files: AnyUppyFile[]) {
		const total = files.length;

		const promises = files.map((file, i) => {
			const current = i + 1;

			if ('error' in file && file.error) throw new Error(file.error);

			if (file.isRemote) {
				// We are not doing remote files atm
				// will need to implment all the socket stuff from uppy's tus in order to get it
				throw new Error('remote files not currently supported');
			}

			// Don't double-emit upload-started for Golden Retriever-restored files that were already started
			if (!file.progress?.uploadStarted || !file.isRestored) {
				this.uppy.emit('upload-started', file);
			}

			return this.upload(file, current, total);
		});

		return settle(promises);
	}

	/**
	 * Install tus plugin
	 */
	install() {
		this.uppy.setState({
			capabilities: {
				...this.uppy.getState().capabilities,
				resumableUploads: true,
			},
		});

		this.uppy.addUploader(this.handleUpload);

		this.uppy
			// @ts-ignore
			.on('file-added', this.handleFileAdded)
			.on('cancel-all', this.handleCancelAll)
			// @ts-ignore
			.on('pause-all', this.handlePauseAll)
			// @ts-ignore
			.on('file-removed', this.handleRemove)
			.on('reset-progress', this.handleResetProgress)
			// @ts-ignore
			.on('resume-all', this.handleResumeAll)
			// @ts-ignore
			.on('upload-pause', this.handleUploadPause);
	}

	/**
	 * Uninstall tus plugin
	 */
	uninstall() {
		this.queue.dispose();

		this.uppy.setState({
			capabilities: {
				...this.uppy.getState().capabilities,
				resumableUploads: false,
			},
		});

		this.uppy.removeUploader(this.handleUpload);

		this.uppy
			// @ts-ignore
			.off('file-added', this.handleFileAdded)
			.off('cancel-all', this.handleCancelAll)
			// @ts-ignore
			.off('pause-all', this.handlePauseAll)
			// @ts-ignore
			.off('file-removed', this.handleRemove)
			.off('reset-progress', this.handleResetProgress)
			// @ts-ignore
			.off('resume-all', this.handleResumeAll)
			// @ts-ignore
			.off('upload-pause', this.handleUploadPause);
	}
}

export default Tus;
